import { createRef } from "react";
import axios from "axios";

import { domain } from "./constants";
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from "./utils";

export const authContextRef = createRef();

const backend = axios.create();

// Separate instance for auth/public endpoints (login, validate, logged, token, logout).
// No JWT and no 401 retry interceptor — using `backend` here would loop on refresh.
const authBackend = axios.create({
  baseURL: `${domain}/api`,
});

authBackend.interceptors.request.use(config => {
  config.headers["x-tenant-id"] = "binimeliz";
  return config;
});

/**
 * Renueva la sesión. UNA a la vez (SEGURIDAD #8).
 *
 * Desde que el refresh token es ROTATORIO —se usa una vez y muere— esta función no puede
 * correr dos veces en paralelo. El dashboard dispara varias consultas juntas; cuando el
 * access token vence, todas responden 401 casi al mismo tiempo. Sin este candado, la
 * primera rota el token y las demás mandan el ya rotado, que el servidor —con razón—
 * lee como un reuso y cierra la sesión entera. O sea: sin esto, el usuario se
 * desloguearía solo cada cinco minutos, y el motivo se vería como un incidente de
 * seguridad.
 *
 * El candado es la promesa en curso: el que llega mientras hay una renovación andando se
 * cuelga de ella en vez de empezar otra.
 */
let renovacionEnCurso = null;

const getToken = async () => {
  if (renovacionEnCurso) return renovacionEnCurso;

  renovacionEnCurso = (async () => {
    try {
      const refreshToken = getRefreshToken();
      const response = await authBackend.post("/user/token", { refreshToken });
      const token = response.data.accessToken;
      if (token) setAccessToken(token);
      // El servidor rota: el refresh que se acaba de usar ya no sirve. Guardar el nuevo
      // no es opcional — mandar el viejo otra vez es lo que el servidor lee como robo.
      if (response.data.refreshToken) setRefreshToken(response.data.refreshToken);
      return token;
    } catch (err) {
      console.log(err);
      return undefined;
    } finally {
      renovacionEnCurso = null;
    }
  })();

  return renovacionEnCurso;
};

export { authBackend };

backend.interceptors.request.use(
  async config => {
    const accessToken = await getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    config.headers["x-tenant-id"] = "binimeliz";
    // Modo "Ver como" FIEL (auditoría 28-ago): si el admin activó el lente,
    // cada request lleva x-vista-como y el backend aplica los permisos del rol
    // simulado. Se lee de sessionStorage directo (misma clave que App.js,
    // VISTA_COMO_KEY = "bini_vista_como") para no importar React/el contexto acá.
    try {
      const vistaComo = sessionStorage.getItem("bini_vista_como");
      if (vistaComo) config.headers["x-vista-como"] = vistaComo;
    } catch {}
    if (!config._retry) {
      config.url = `${domain}/api/${config.url}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

backend.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config || {};
    const status = error.response?.status;
    // El backend marca con sesionTerminada:true SOLO los 401 de token
    // ausente/vencido/inválido (check-auth). Un 403 (sin permiso) NO lo lleva.
    const sesionTerminada = error.response?.data?.sesionTerminada === true;

    // 401 por access token vencido: intentar UN refresh silencioso y reintentar.
    if (status === 401 && !config._retry && getRefreshToken()) {
      config._retry = true;

      // Si mientras esta consulta viajaba alguien YA renovó, no hay nada que renovar:
      // esta request salió con el token viejo y por eso volvió con 401. Se reintenta con
      // el que hay. Sin este atajo, cada tanda de consultas que arranca antes de la
      // renovación y muere después gasta una rotación de más — funciona igual, pero
      // quema tokens y llena la bitácora de ruido.
      const usado = String(config.headers?.Authorization || "").replace("Bearer ", "");
      const vigente = getAccessToken();
      if (vigente && usado && vigente !== usado) {
        return backend(config);
      }

      const accessToken = await getToken();
      if (accessToken) {
        setAccessToken(accessToken);
        return backend(config);
      }
      // El refresh falló → la sesión terminó de verdad; cae al logout de abajo.
    }

    // Expulsar al login SOLO si la sesión terminó (flag del backend, o un 401 que
    // sobrevivió al refresh). Un 403 u otros errores NO expulsan: se propagan para
    // que el componente los muestre inline ("no tiene permiso para X").
    if (status === 401 && (sesionTerminada || config._retry)) {
      authContextRef.current?.logout();
    }
    return Promise.reject(error);
  }
);

export default backend;
